import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Badge, Button, Col, Input, Label, Row, Spinner, Table } from "reactstrap";
import Select from "react-select";

import PageHero, { PageHeroExportButton, PageHeroRefreshButton } from "../../../components/PageHero";
import FilterBand from "../../../components/FilterBand";
import SectionCard from "../../../components/SectionCard";
import MetricCard from "../../../components/MetricCard";
import MetricCardGroup from "../../../components/MetricCardGroup";
import SortHeader from "../../../components/SortHeader";
import TablePagination from "../../../components/TablePagination";
import Status from "../../../components/Status";
import ShellCard from "../../../components/ShellCard";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { useAuth } from "../../../hooks/auth";
import { useCompanies } from "../../../hooks/companies";
import { useToast } from "../../../hooks/toast";
import api from "../../../services/api";
import { exportCsv } from "../../../utils/exportCsv";
import { reactSelectPortalProps, reactSelectStyles } from "../../../utils/reactSelectStyles";
import { paginate, sortRows, toggleSort } from "../../../utils/tableHelpers";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import EmbarcadorCadastro from "./EmbarcadorCadastro";
import EmbarcadorDetalheModal from "./EmbarcadorDetalheModal";

// Tela de Embarcadores no padrão do Hub (PO1-2728): PageHero + FilterBand +
// MetricCard + Table padrão com SortHeader/TablePagination, tudo por token
// (AUTOPILOT_COLORS / --everlog-*). Endpoints, parâmetros e regras são os mesmos
// de antes; só a apresentação mudou.
//
// Sprint 638: para o cliente, "Embarcadores" é sempre o cadastro DELE. A tela
// abre direto no embarcador escolhido no topo, em página, com as abas do
// cadastro (Dados gerais, Origens / CDs, Contatos). A lista continua
// só para a Everlog (setor admin), e o detalhe dela traz as mesmas abas.
// A aba vem da URL (?aba=origens / ?aba=contatos), que é para onde os menus
// antigos de Parâmetros › Origens / CDs e Contatos redirecionam.

export const ABA_DA_URL = "aba";

export function abaDaUrl(search) {
  const valor = new URLSearchParams(search || "").get(ABA_DA_URL);
  return valor ? valor.trim() : undefined;
}

const formatDocument = (value) => {
  const digits = String(value || "").replace(/\D/g, "");

  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }

  if (digits.length === 14) {
    return digits.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "$1.$2.$3/$4-$5"
    );
  }

  return value || "-";
};

const formatCep = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length === 8 ? digits.replace(/(\d{5})(\d{3})/, "$1-$2") : value || "-";
};

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "1", label: "Ativo" },
  { value: "0", label: "Inativo" },
];

const PAGE_SIZES = [10, 20, 50, 100];

const DEFAULT_SORT = { field: "razaoSocial", dir: "asc" };

const EXPORT_COLUMNS = [
  { key: "razaoSocial", label: "Razão social" },
  { key: "fantasia", label: "Fantasia" },
  { key: "cnpjCpfFormatado", label: "Documento" },
  { key: "statusLabel", label: "Status" },
];

// ─── estilos por token ────────────────────────────────────────────────────────
const asideLabelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.74rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  marginBottom: "0.35rem",
};

const asideValueStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "1.2rem",
  fontWeight: 700,
  lineHeight: 1.1,
  marginBottom: "0.25rem",
};

const workspaceGridStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.7fr) minmax(320px, 0.95fr)",
  gap: "1rem",
  alignItems: "start",
};

const fieldLabelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.76rem",
  marginBottom: "0.35rem",
};

const chipRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "0.45rem",
  marginTop: "0.75rem",
};

const filterChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.35rem",
  borderRadius: 999,
  padding: "0.38rem 0.72rem",
  background: AUTOPILOT_COLORS.surfacePill,
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  color: AUTOPILOT_COLORS.text,
  fontSize: "0.78rem",
};

const errorBoxStyle = {
  marginTop: "1rem",
  background: AUTOPILOT_COLORS.statusDangerBg,
  border: `1px solid ${AUTOPILOT_COLORS.statusDangerBorder}`,
  borderRadius: 12,
  padding: "0.9rem 1rem",
  color: AUTOPILOT_COLORS.title,
  fontSize: "0.84rem",
};

const stateBoxStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.6rem",
  padding: "2rem 1rem",
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.88rem",
  textAlign: "center",
};

const previewFieldLabelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.74rem",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  marginBottom: "0.25rem",
};

const previewFieldValueStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "0.92rem",
  lineHeight: 1.45,
};

const focusCardStyle = {
  border: "1px solid var(--theme-accent-border)",
  borderRadius: 12,
  padding: "0.85rem 0.95rem",
  background: "var(--everlog-card-surface-field)",
  marginBottom: "1rem",
};

const selectedRowStyle = {
  background: "var(--everlog-card-surface-active)",
  boxShadow: "inset 0 0 0 1px var(--theme-accent-border)",
};

function PreviewField({ label, value }) {
  if (!value) return null;

  return (
    <div style={{ marginBottom: "0.85rem" }}>
      <div style={previewFieldLabelStyle}>{label}</div>
      <div style={previewFieldValueStyle}>{value}</div>
    </div>
  );
}

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const ListaEmbarcadores = () => {
  const { empresa, empresaId, hasEmpresaSelection } = useCompanies();
  const location = useLocation();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [data, setData] = useState([]);
  const [filtroCnpjCpf, setFiltroCnpjCpf] = useState("");
  const [filtroRazaoSocial, setFiltroRazaoSocial] = useState("");
  const [filtroFantasia, setFiltroFantasia] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");
  // Busca unificada da faixa: filtra em memória a base carregada (razão social,
  // fantasia e documento). Os campos do painel continuam indo para a API.
  const [buscaInput, setBuscaInput] = useState("");
  const [busca, setBusca] = useState("");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [selectedEmbarcadorId, setSelectedEmbarcadorId] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [embarcadorDetalhe, setEmbarcadorDetalhe] = useState(null);
  const [abaDetalhe, setAbaDetalhe] = useState(undefined);

  // Link antigo (Parâmetros › Origens / Contatos) chega com ?aba=: abre o
  // detalhe do embarcador do topo já na aba pedida.
  useEffect(() => {
    const aba = abaDaUrl(location.search);
    if (aba && hasEmpresaSelection) {
      setAbaDetalhe(aba);
      setEmbarcadorDetalhe(empresaId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregar = useCallback(
    async (overrides = {}) => {
      try {
        setLoading(true);
        setLoadError("");

        const params = {};
        const embarcadorId =
          "embarcadorId" in overrides ? overrides.embarcadorId : empresa?.value;
        const cnpjCpf = "cnpjCpf" in overrides ? overrides.cnpjCpf : filtroCnpjCpf;
        const razaoSocial =
          "razaoSocial" in overrides ? overrides.razaoSocial : filtroRazaoSocial;
        const fantasia = "fantasia" in overrides ? overrides.fantasia : filtroFantasia;
        const status = "status" in overrides ? overrides.status : filtroStatus;

        if (embarcadorId) params.embarcadorId = embarcadorId;
        if (cnpjCpf) params.cnpjCpf = cnpjCpf;
        if (razaoSocial) params.razaoSocial = razaoSocial;
        if (fantasia) params.fantasia = fantasia;
        if (status !== "") params.status = status;

        const response = await api.get("/cadastros-gerais/embarcadores", {
          params,
        });

        const rows = (response.data || []).map((item) => ({
          ...item,
          cnpjCpfFormatado: formatDocument(item.cnpjCpf),
          cnpjCpfDigits: String(item.cnpjCpf || "").replace(/\D/g, ""),
          statusLabel: item.status ? "Ativo" : "Inativo",
        }));

        setData(rows);
        setPage(1);
        setSelectedEmbarcadorId((current) =>
          rows.some((item) => item.id === current) ? current : rows[0]?.id || null
        );
      } catch (error) {
        const mensagem =
          error?.response?.data?.error ||
          "Nao foi possivel carregar a base de embarcadores para este contexto.";
        setData([]);
        setSelectedEmbarcadorId(null);
        setPreviewData(null);
        setPreviewError("");
        setLoadError(mensagem);
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro ao carregar embarcadores",
          description: mensagem,
          icon: "tim-icons icon-alert-circle-exc",
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, empresa, filtroCnpjCpf, filtroFantasia, filtroRazaoSocial, filtroStatus]
  );

  useEffect(() => {
    carregar();
    // Recarrega só quando o contexto (empresa) muda; filtros avançados disparam
    // pelo Aplicar/Limpar/tiles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresa]);

  useEffect(() => {
    if (!selectedEmbarcadorId) {
      setPreviewData(null);
      setPreviewError("");
      return;
    }

    let mounted = true;

    async function carregarPreview() {
      try {
        setPreviewLoading(true);
        setPreviewError("");
        const response = await api.get(`/embarcadores/${selectedEmbarcadorId}/detalhe`);

        if (mounted) {
          setPreviewData(response.data || null);
        }
      } catch (error) {
        if (mounted) {
          setPreviewData(null);
          setPreviewError(
            error?.response?.data?.error ||
              "Nao foi possivel carregar o resumo deste embarcador."
          );
        }
      } finally {
        if (mounted) {
          setPreviewLoading(false);
        }
      }
    }

    carregarPreview();

    return () => {
      mounted = false;
    };
  }, [selectedEmbarcadorId]);

  const limpar = () => {
    setFiltroCnpjCpf("");
    setFiltroRazaoSocial("");
    setFiltroFantasia("");
    setFiltroStatus("");
    setBuscaInput("");
    setBusca("");
    carregar({
      cnpjCpf: "",
      razaoSocial: "",
      fantasia: "",
      status: "",
    });
  };

  const aplicarStatus = (nextStatus) => {
    setFiltroStatus(nextStatus);
    carregar({ status: nextStatus });
  };

  const handleOrdenar = (field) => {
    setSort((current) => toggleSort(current, field));
    setPage(1);
  };

  const selectedRow = useMemo(
    () => data.find((item) => item.id === selectedEmbarcadorId) || null,
    [data, selectedEmbarcadorId]
  );

  const totais = useMemo(() => {
    const ativos = data.filter((item) => item.status).length;
    const inativos = data.length - ativos;
    const comFantasia = data.filter((item) => item.fantasia).length;

    return {
      total: data.length,
      ativos,
      inativos,
      comFantasia,
    };
  }, [data]);

  const activeFilters = useMemo(
    () =>
      [
        filtroCnpjCpf && { label: "Documento", value: filtroCnpjCpf },
        filtroRazaoSocial && { label: "Razao social", value: filtroRazaoSocial },
        filtroFantasia && { label: "Fantasia", value: filtroFantasia },
        filtroStatus !== "" && {
          label: "Status",
          value: filtroStatus === "1" ? "Ativo" : "Inativo",
        },
      ].filter(Boolean),
    [filtroCnpjCpf, filtroFantasia, filtroRazaoSocial, filtroStatus]
  );

  // Busca unificada (memória) + ordenação + paginação client-side.
  const filteredRows = useMemo(() => {
    const termo = normalizeText(busca).trim();
    if (!termo) return data;
    const termoDigits = termo.replace(/\D/g, "");
    return data.filter((item) => {
      if (normalizeText(item.razaoSocial).includes(termo)) return true;
      if (normalizeText(item.fantasia).includes(termo)) return true;
      if (termoDigits && item.cnpjCpfDigits.includes(termoDigits)) return true;
      return false;
    });
  }, [data, busca]);

  const sortedRows = useMemo(
    () =>
      sortRows(filteredRows, sort, (row, field) =>
        field === "cnpjCpfFormatado" ? row.cnpjCpfDigits : row[field]
      ),
    [filteredRows, sort]
  );

  const pagina = useMemo(() => paginate(sortedRows, page, pageSize), [sortedRows, page, pageSize]);

  const exportar = () => {
    const ok = exportCsv({ filename: "embarcadores", columns: EXPORT_COLUMNS, rows: sortedRows });
    if (!ok) {
      addToast({
        place: "tr",
        type: "warning",
        title: "Nada para exportar",
        description: "A listagem atual está vazia.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    }
  };

  const previewTitle =
    previewData?.fantasia ||
    previewData?.razaoSocial ||
    selectedRow?.fantasia ||
    selectedRow?.razaoSocial ||
    "Nenhum embarcador selecionado";

  const statusOption = STATUS_OPTIONS.find((opt) => opt.value === filtroStatus) || STATUS_OPTIONS[0];

  const summaryCards = [
    {
      key: "total",
      label: "Base carregada",
      value: totais.total,
      helper: "Embarcadores no recorte atual",
      status: null,
      tone: "info",
      icon: "tim-icons icon-bank",
    },
    {
      key: "ativos",
      label: "Ativos",
      value: totais.ativos,
      helper: "Disponiveis para operacao",
      status: "1",
      tone: "success",
      icon: "tim-icons icon-check-2",
    },
    {
      key: "inativos",
      label: "Inativos",
      value: totais.inativos,
      helper: "Exigem revisao cadastral",
      status: "0",
      tone: "warning",
      icon: "tim-icons icon-alert-circle-exc",
    },
    {
      key: "fantasia",
      label: "Com fantasia",
      value: totais.comFantasia,
      helper: "Indicador simples de completude",
      status: null,
      tone: "neutral",
      icon: "tim-icons icon-tag",
    },
  ];

  const colunas = [
    { key: "razaoSocial", label: "Embarcador", sortField: "razaoSocial" },
    { key: "cnpjCpfFormatado", label: "Documento", sortField: "cnpjCpfFormatado" },
    { key: "statusLabel", label: "Status", sortField: "statusLabel" },
    { key: "acoes", label: "", sortField: null },
  ];

  return (
    <div className="content embarcadores-page">
      <EmbarcadorDetalheModal
        embarcadorId={embarcadorDetalhe}
        abaInicial={abaDetalhe}
        onClose={() => {
          setEmbarcadorDetalhe(null);
          setAbaDetalhe(undefined);
        }}
      />

      <PageHero
        eyebrow="Base de embarcadores"
        title="Embarcadores"
        description="Use a tela para localizar rapidamente embarcadores, revisar o status da base e aprofundar detalhes sem cair direto em uma tabela fria."
        actions={
          <>
            <PageHeroRefreshButton onClick={() => carregar()} loading={loading} />
            <PageHeroExportButton onClick={exportar} disabled={loading || sortedRows.length === 0} />
          </>
        }
      />

      <FilterBand
        mode="server"
        searchLabel="Buscar embarcador"
        searchHint="Razão social, fantasia ou documento"
        searchPlaceholder="Digite razão social, fantasia, CNPJ ou CPF"
        searchValue={buscaInput}
        onSearchChange={setBuscaInput}
        onSearchSubmit={(valor) => {
          setBusca(valor);
          setPage(1);
        }}
        activeFiltersCount={activeFilters.length}
        panelTitle="Filtros"
        panelDescription="Refine o recorte por documento, nomes cadastrais e status. O resultado representa a base filtrada pela API."
        onApply={() => carregar()}
        onClear={limpar}
        busy={loading}
      >
        <Row>
          <Col lg="3" md="6" sm="12">
            <Label style={fieldLabelStyle}>CNPJ/CPF</Label>
            <Input
              value={filtroCnpjCpf}
              onChange={(e) => setFiltroCnpjCpf(e.target.value)}
              placeholder="Buscar por CNPJ ou CPF..."
            />
          </Col>
          <Col lg="3" md="6" sm="12">
            <Label style={fieldLabelStyle}>Razao Social</Label>
            <Input
              value={filtroRazaoSocial}
              onChange={(e) => setFiltroRazaoSocial(e.target.value)}
              placeholder="Buscar por razao social..."
            />
          </Col>
          <Col lg="3" md="6" sm="12">
            <Label style={fieldLabelStyle}>Fantasia</Label>
            <Input
              value={filtroFantasia}
              onChange={(e) => setFiltroFantasia(e.target.value)}
              placeholder="Buscar por fantasia..."
            />
          </Col>
          <Col lg="3" md="6" sm="12">
            <Label style={fieldLabelStyle}>Status</Label>
            <Select
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={STATUS_OPTIONS}
              value={statusOption}
              onChange={(opt) => setFiltroStatus(opt ? opt.value : "")}
              isSearchable={false}
              placeholder="Todos"
            />
          </Col>
        </Row>

        {activeFilters.length > 0 && (
          <div style={chipRowStyle}>
            {activeFilters.map((item) => (
              <div key={item.label} style={filterChipStyle}>
                <strong>{item.label}:</strong>
                <span>{item.value}</span>
              </div>
            ))}
          </div>
        )}
      </FilterBand>

      <MetricCardGroup
        title="Situação dos embarcadores"
        description="Ativos e Inativos filtram a tabela; clique de novo para desfazer."
      >
        {summaryCards.map((card) => {
          const isFilterCard = card.status !== null;
          const isActive = isFilterCard && filtroStatus === card.status;
          return (
            <MetricCard
              key={card.key}
              label={card.label}
              value={card.value}
              helper={card.helper}
              iconClassName={card.icon}
              tone={card.tone}
              active={isActive}
              onClick={
                isFilterCard ? () => aplicarStatus(filtroStatus === card.status ? "" : card.status) : undefined
              }
            />
          );
        })}
      </MetricCardGroup>

      {loadError && (
        <div role="alert" style={errorBoxStyle}>
          {loadError}
        </div>
      )}

      <ShellCard>
          <div style={workspaceGridStyle}>
            <div style={{ minWidth: 0 }}>
              <SectionCard
                title="Carteira carregada"
                description="Clique em uma linha ou use o botao de resumo para alimentar o painel lateral sem sair da listagem."
                action={
                  <span style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                    {sortedRows.length} resultado(s)
                  </span>
                }
              >
                {loading ? (
                  <div style={stateBoxStyle}>
                    <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
                    <span>Carregando embarcadores…</span>
                  </div>
                ) : sortedRows.length === 0 ? (
                  <div style={stateBoxStyle}>
                    {data.length === 0
                      ? "Nenhum embarcador encontrado neste recorte."
                      : "Nenhum embarcador corresponde à busca."}
                  </div>
                ) : (
                  <>
                    <Table size="sm" responsive hover className="embarcadores-table hub-thead-accent">
                      <thead className="text-primary">
                        <tr>
                          {colunas.map((col) => (
                            <th key={col.key} className={col.key === "acoes" ? "text-center" : undefined}>
                              {col.sortField ? (
                                <SortHeader
                                  label={col.label}
                                  field={col.sortField}
                                  sort={sort}
                                  onSort={handleOrdenar}
                                />
                              ) : (
                                col.label
                              )}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pagina.items.map((row) => {
                          const selecionado = row.id === selectedEmbarcadorId;
                          return (
                            <tr
                              key={row.id}
                              onClick={() => setSelectedEmbarcadorId(row.id)}
                              style={{ cursor: "pointer", ...(selecionado ? selectedRowStyle : {}) }}
                              aria-selected={selecionado}
                            >
                              <td>
                                <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, marginBottom: "0.15rem" }}>
                                  {row.razaoSocial || "-"}
                                </div>
                                <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem", lineHeight: 1.35 }}>
                                  {row.fantasia || "Sem nome fantasia"}
                                </div>
                              </td>
                              <td style={{ color: AUTOPILOT_COLORS.text, whiteSpace: "nowrap" }}>
                                {row.cnpjCpfFormatado}
                              </td>
                              <td>
                                <Status status={row.statusLabel} title={row.statusLabel} />
                              </td>
                              <td>
                                <div
                                  onClick={(event) => event.stopPropagation()}
                                  style={{ display: "flex", gap: "0.45rem", justifyContent: "center", flexWrap: "wrap" }}
                                >
                                  <Button
                                    size="sm"
                                    color={selecionado ? "primary" : "secondary"}
                                    outline={!selecionado}
                                    onClick={() => setSelectedEmbarcadorId(row.id)}
                                    style={{ whiteSpace: "nowrap" }}
                                  >
                                    Resumo
                                  </Button>
                                  <Button
                                    size="sm"
                                    color="info"
                                    outline
                                    onClick={() => setEmbarcadorDetalhe(row.id)}
                                    style={{ whiteSpace: "nowrap" }}
                                  >
                                    Detalhes
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    <TablePagination
                      page={pagina.page}
                      pageSize={pageSize}
                      total={pagina.total}
                      pageSizes={PAGE_SIZES}
                      itemLabel="embarcador(es)"
                      onPageChange={setPage}
                      onPageSizeChange={(novo) => {
                        setPageSize(novo);
                        setPage(1);
                      }}
                    />
                  </>
                )}
              </SectionCard>
            </div>

            <div style={{ minWidth: 0 }}>
              <SectionCard
                title="Resumo do embarcador"
                description="Painel de leitura rapida para confirmar identidade, status e cobertura cadastral antes de abrir os detalhes completos."
              >
                {!selectedEmbarcadorId && !loading && (
                  <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.84rem" }}>
                    Nenhum embarcador selecionado neste recorte.
                  </div>
                )}

                {selectedEmbarcadorId && (
                  <>
                    <div style={focusCardStyle}>
                      <div style={asideLabelStyle}>Em foco</div>
                      <div style={{ ...asideValueStyle, fontSize: "1.1rem", marginBottom: "0.35rem" }}>
                        {previewTitle}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                        {selectedRow?.statusLabel && (
                          <Badge color={selectedRow.status ? "success" : "warning"} pill>
                            {selectedRow.statusLabel}
                          </Badge>
                        )}
                        <Badge color="secondary" pill>
                          {selectedRow?.cnpjCpfFormatado || formatDocument(previewData?.cnpjCpf)}
                        </Badge>
                      </div>
                    </div>

                    {previewLoading ? (
                      <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
                        <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
                      </div>
                    ) : previewError ? (
                      <div style={{ ...errorBoxStyle, marginTop: 0, marginBottom: "1rem" }}>{previewError}</div>
                    ) : (
                      <>
                        <PreviewField
                          label="Razao social"
                          value={previewData?.razaoSocial || selectedRow?.razaoSocial}
                        />
                        <PreviewField
                          label="Fantasia"
                          value={previewData?.fantasia || selectedRow?.fantasia}
                        />
                        <PreviewField
                          label="Inscricao estadual"
                          value={previewData?.inscricaoEstadual}
                        />
                        <PreviewField
                          label="Inscricao municipal"
                          value={previewData?.inscricaoMunicipal}
                        />
                        <PreviewField label="Suframa" value={previewData?.codSuframa} />
                        <PreviewField
                          label="Endereco"
                          value={[
                            previewData?.logradouro,
                            previewData?.numero,
                            previewData?.complemento,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        />
                        <PreviewField
                          label="Bairro / Cidade / UF"
                          value={[
                            previewData?.bairro,
                            previewData?.cidade,
                            previewData?.uf,
                          ]
                            .filter(Boolean)
                            .join(" - ")}
                        />
                        <PreviewField label="CEP" value={formatCep(previewData?.cep)} />
                        <PreviewField label="Observacao" value={previewData?.observacao} />
                        <PreviewField
                          label="Origens / CDs"
                          value={
                            previewData?.filiais
                              ? `${previewData.filiais.length} origem(ns) / CD(s)`
                              : null
                          }
                        />
                      </>
                    )}

                    <div style={{ marginTop: "1rem", display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
                      <Button color="info" size="sm" onClick={() => setEmbarcadorDetalhe(selectedEmbarcadorId)}>
                        Abrir detalhes completos
                      </Button>
                      <Button
                        color="secondary"
                        size="sm"
                        outline
                        onClick={() => carregar()}
                        disabled={loading}
                      >
                        Atualizar base
                      </Button>
                    </div>
                  </>
                )}
              </SectionCard>
            </div>
          </div>
      </ShellCard>
    </div>
  );
};

// Página do cliente: o cadastro do embarcador escolhido no topo.
const CadastroDoEmbarcador = () => {
  const { empresa, empresaId, hasEmpresaSelection } = useCompanies();
  const location = useLocation();
  const history = useHistory();
  const aba = abaDaUrl(location.search);
  const [dados, setDados] = useState(null);

  useEffect(() => {
    setDados(null);
  }, [empresaId]);

  const trocarAba = (id) => {
    const params = new URLSearchParams(location.search || "");
    params.set(ABA_DA_URL, id);
    history.replace({ pathname: location.pathname, search: `?${params.toString()}` });
  };

  const nome = (dados && (dados.fantasia || dados.razaoSocial)) || (empresa && empresa.label) || "Embarcador";

  return (
    <div className="content embarcadores-page">
      <PageHero
        eyebrow="Cadastro do embarcador"
        title={hasEmpresaSelection ? nome : "Embarcador"}
        description="Dados do embarcador, filiais, origens / CDs de onde a mercadoria sai e os contatos que recebem os e-mails. Os parâmetros ficam em Parâmetros › Embarcador."
      />
      {!hasEmpresaSelection ? (
        <div style={{ marginTop: "1.25rem" }}>
          <SpecificEmpresaRequiredNotice message="O cadastro é por embarcador: escolha um embarcador específico no topo da página." />
        </div>
      ) : (
        <ShellCard>
          <EmbarcadorCadastro
            key={empresaId}
            embarcadorId={empresaId}
            embarcadorNome={empresa && empresa.label}
            aba={aba}
            onAbaChange={trocarAba}
            onDados={setDados}
          />
        </ShellCard>
      )}
    </div>
  );
};

const Embarcadores = () => {
  const { user } = useAuth();
  return user && user.setor === "admin" ? <ListaEmbarcadores /> : <CadastroDoEmbarcador />;
};

export default Embarcadores;
